// Pure logic for the Teams-style tile highlight on the practice screen.
//
// One participant tile "lights up" when its persona is asking a question:
//   bright — the question audio is playing right now
//   soft   — L2 ONLY: a question is pending and the user has not clicked yet.
//            L3 does not use it; see participantGlow.
//   none   — nothing pending for this tile
//
// Two question engines feed this (see NewRecordPresentation.jsx):
//   L3 sequential Q&A (isQAPractice): qaSession.sessionState drives phases —
//     MURPHY_ASKING while TTS plays, AWAITING_ANSWER while the VAD listens.
//   L2 interactive: currentInteraction marks a pending question for a named
//     persona; isPlayingAudio is true only while its TTS buffer plays. L2 is
//     click-to-hear, so a pending interaction with no audio playing is the
//     "waiting for the user to click / answer" phase.
//
// Name matching mirrors the legacy participant-card logic exactly:
// String(a) === String(b) — personas are matched by display name.

import { QA_STATES } from './qaStateMachine';

export const GLOW = {
  NONE: 'none',
  SOFT: 'soft',
  BRIGHT: 'bright',
};

/**
 * Glow state for one participant tile.
 *
 * @param {object} args
 * @param {string} args.name                 tile's participant display name
 * @param {boolean} args.isQAPractice        L3 sequential engine active
 * @param {string} [args.sessionState]       qaSession.sessionState (L3)
 * @param {string} [args.currentQuestionName] qaSession.currentQuestion?.participantName (L3)
 * @param {string} [args.currentInteractionName] currentInteraction?.participantName (L2)
 * @param {boolean} [args.isPlayingAudio]    L2 TTS currently playing
 * @returns {'none'|'soft'|'bright'}
 */
export function participantGlow({
  name,
  isQAPractice,
  sessionState,
  currentQuestionName,
  currentInteractionName,
  isPlayingAudio,
}) {
  if (isQAPractice) {
    if (currentQuestionName == null) return GLOW.NONE;
    if (String(currentQuestionName) !== String(name)) return GLOW.NONE;
    // Bright only while the question audio is actually playing. AWAITING_ANSWER
    // used to hold a soft glow, but once the turn has passed to the user a tile
    // still ringed gold reads as "this persona is talking" when they are not.
    // The bar strip says "Listening…" and the user's own tile takes the green
    // speaking ring, so nothing is lost by dropping it.
    if (sessionState === QA_STATES.MURPHY_ASKING) return GLOW.BRIGHT;
    return GLOW.NONE;
  }

  if (currentInteractionName == null) return GLOW.NONE;
  if (String(currentInteractionName) !== String(name)) return GLOW.NONE;
  return isPlayingAudio ? GLOW.BRIGHT : GLOW.SOFT;
}

/**
 * The caption to show inside a lit tile, or null for none.
 *
 * L3 deliberately shows nothing. The question is spoken aloud, and printing it
 * on the tile let the rep read it back rather than listen and respond, which
 * is not what the practice is for. The glow still carries who is asking and
 * whether they are waiting on an answer.
 *
 * L2 is click-to-hear, so its lit tile keeps a prompt. That one is an
 * instruction rather than the question itself, and without it nothing tells
 * the user the tile is clickable.
 */
export function tileCaption({ glow, isQAPractice }) {
  if (glow === GLOW.NONE) return null;
  if (isQAPractice) return null;
  return 'Click to hear the question';
}
